import gsap from 'gsap';
import type { Animation, SlideDoc } from '../types/slide-doc';

type ShapeRefMap = Map<string, Element>;

// Builds a GSAP timeline from a slide's animations[] and the live shape refs.
// Returns the timeline so caller can play/pause/kill.
export function buildSlideTimeline(slide: SlideDoc, refs: ShapeRefMap): gsap.core.Timeline {
  const tl = gsap.timeline({ paused: true });
  const anims = slide.animations ?? [];

  for (const a of anims) {
    const el = refs.get(a.target);
    if (!el) {
      console.warn(`[animation] target not found: ${a.target}`);
      continue;
    }
    addAnimation(tl, el, a);
  }

  return tl;
}

function addAnimation(tl: gsap.core.Timeline, el: Element, a: Animation) {
  const t = a.atMs / 1000;
  const d = a.durationMs / 1000;
  const ease = a.ease ?? 'power2.out';

  switch (a.effect) {
    case 'fadeIn':
      tl.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: d, ease }, t);
      break;
    case 'fadeOut':
      tl.to(el, { autoAlpha: 0, duration: d, ease }, t);
      break;
    case 'scalePop': {
      const peak = a.peakScale ?? 1.1;
      const to = a.toScale ?? 1;
      const from = a.fromScale ?? 0.8;
      tl.fromTo(
        el,
        { scale: from, transformOrigin: '50% 50%' },
        { scale: peak, duration: d * 0.55, ease: 'power3.out' },
        t
      );
      tl.to(el, { scale: to, duration: d * 0.45, ease: 'power2.inOut' }, t + d * 0.55);
      break;
    }
    case 'scaleTo':
      tl.to(
        el,
        { scale: a.toScale ?? 1, transformOrigin: '50% 50%', duration: d, ease },
        t
      );
      break;
    case 'rotate':
      tl.to(el, { rotation: a.toRotation ?? 360, duration: d, ease }, t);
      break;
    case 'moveTo':
      tl.to(el, { x: a.toX ?? 0, y: a.toY ?? 0, duration: d, ease }, t);
      break;
    case 'slideIn': {
      const fx = a.fromOffsetX ?? 0;
      const fy = a.fromOffsetY ?? -80;
      tl.fromTo(
        el,
        { x: fx, y: fy, autoAlpha: 0 },
        { x: 0, y: 0, autoAlpha: 1, duration: d, ease },
        t
      );
      break;
    }
    case 'drawStroke': {
      // circle's <circle> child holds the stroke-dasharray / dashoffset
      const circle = (el as SVGSVGElement).querySelector('circle');
      if (!circle) break;
      const circumference = Number(circle.getAttribute('data-circumference') ?? 0);
      tl.fromTo(
        circle,
        { strokeDashoffset: circumference },
        { strokeDashoffset: 0, duration: d, ease },
        t
      );
      break;
    }
  }
}
